(() => {
  'use strict';

  class LibertineCollection extends HTMLElement {
    connectedCallback() {
      this._sectionId    = this.dataset.sectionId;
      this._collectionUrl = this.dataset.collectionUrl;
      this._rootUrl      = this._collectionUrl;
      this._perPage      = parseInt(this.dataset.perPage, 10) || 48;
      this._page         = 1;
      this._loading      = false;
      this._hasMore      = false;
      this._countAbort   = null;

      // DOM refs
      this._grid        = this.querySelector(`#clp-products-${this._sectionId}`);
      this._gridWrapper = this.querySelector(`#clp-grid-${this._sectionId}`);
      this._sentinel    = this.querySelector('.clp__sentinel');
      this._panel       = this.querySelector('.clp__panel');
      this._overlay     = this.querySelector('.clp__panel-overlay');
      this._drawer      = this.querySelector('.clp__panel-drawer');
      this._filterBtn   = this.querySelector('.clp__filter-btn');
      this._closeBtn    = this.querySelector('.clp__panel-close');
      this._applyBtn    = this.querySelector('.clp__apply-btn');
      this._clearBtn    = this.querySelector('.clp__clear-btn');
      this._form        = this.querySelector('.clp__filter-form');
      this._nav         = this.querySelector('.clp__nav');

      // Init applied params from current URL (supports pre-filtered page loads)
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete('page');
      this._appliedParams = urlParams.toString();

      // Read initial state from server-rendered HTML
      this._hasMore = this._gridWrapper?.dataset.hasNext === 'true';
      this._syncApplyBtn(parseInt(this._gridWrapper?.dataset.count ?? 0, 10));

      this._bindPanel();
      this._bindFilters();
      this._bindNav();
      this._initInfiniteScroll();
      this._restoreSubcatContext();
    }

    // ─────────────────────────────────────────────────
    // Subcategory nav (AJAX swap instead of full navigation)
    // ─────────────────────────────────────────────────

    _navStorageKey() {
      return `clp_subcat_ctx_${this._sectionId}`;
    }

    // A page reload (or a direct/bookmarked link) lands on the subcategory's own
    // URL as a normal server render, so it loses the parent's nav context. If we
    // remember having AJAX-navigated here from a parent in this tab, restore that
    // parent's nav (title + subcat links) around the already-correct product grid.
    async _restoreSubcatContext() {
      let state;
      try {
        const raw = sessionStorage.getItem(this._navStorageKey());
        state = raw ? JSON.parse(raw) : null;
      } catch (e) {
        return;
      }

      if (!state || !state.parentUrl || !state.activeUrl) return;
      if (state.activeUrl !== this._collectionUrl || state.parentUrl === this._collectionUrl) return;

      try {
        const res  = await fetch(`${state.parentUrl}?section_id=${this._sectionId}`);
        const text = await res.text();
        const doc  = new DOMParser().parseFromString(text, 'text/html');
        const newNav = doc.querySelector('.clp__nav');
        if (!newNav || !this._nav) return;

        this._nav.innerHTML = newNav.innerHTML;
        this._nav.querySelectorAll('.clp__subcat-link').forEach((el) => {
          el.classList.toggle('clp__subcat-link--active', el.dataset.navUrl === this._collectionUrl);
        });

        this._rootUrl = state.parentUrl;

        // .clp__filter-btn was replaced along with the rest of the nav markup
        this._filterBtn = this._nav.querySelector('.clp__filter-btn');
        this._filterBtn?.addEventListener('click', () => this._openPanel());
      } catch (e) {
        console.warn('[clp] restore subcategory context failed', e);
      }
    }

    _bindNav() {
      this._nav?.addEventListener('click', (e) => {
        const link = e.target.closest('.clp__nav-link');
        if (!link) return;

        const url = link.dataset.navUrl;
        if (!url || url === this._collectionUrl) {
          e.preventDefault();
          return;
        }

        e.preventDefault();
        this._switchCollection(url, link);
      });
    }

    async _switchCollection(url, clickedLink) {
      if (this._loading) return;
      this._loading = true;
      this.classList.add('clp--loading');

      try {
        const res  = await fetch(`${url}?section_id=${this._sectionId}`);
        const text = await res.text();
        const doc  = new DOMParser().parseFromString(text, 'text/html');

        const newWrapper = doc.getElementById(`clp-grid-${this._sectionId}`);
        const newGrid     = doc.getElementById(`clp-products-${this._sectionId}`);

        if (!newWrapper || !newGrid) {
          window.location.href = url;
          return;
        }

        if (this._grid) this._grid.innerHTML = newGrid.innerHTML;

        this._collectionUrl = url;
        this._page           = 1;
        this._appliedParams  = '';
        this._hasMore         = newWrapper.dataset.hasNext === 'true';
        this._syncApplyBtn(parseInt(newWrapper.dataset.count ?? 0, 10));

        // Refresh filters/facets to match the new collection
        const newForm = doc.querySelector('.clp__filter-form');
        if (newForm && this._form) this._form.innerHTML = newForm.innerHTML;

        // Update active state in the nav
        this._nav?.querySelectorAll('.clp__subcat-link').forEach((el) => {
          el.classList.toggle('clp__subcat-link--active', el === clickedLink);
        });

        try {
          if (url === this._rootUrl) {
            sessionStorage.removeItem(this._navStorageKey());
          } else {
            sessionStorage.setItem(
              this._navStorageKey(),
              JSON.stringify({ parentUrl: this._rootUrl, activeUrl: url })
            );
          }
        } catch (e) {
          // sessionStorage unavailable (private mode, etc.) — reload just won't restore context
        }

        history.pushState({}, '', url);
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {
        console.warn('[clp] collection switch failed', e);
        window.location.href = url;
      } finally {
        this._loading = false;
        this.classList.remove('clp--loading');
        this._recheckSentinel();
      }
    }

    // ─────────────────────────────────────────────────
    // Panel
    // ─────────────────────────────────────────────────

    _bindPanel() {
      this._filterBtn?.addEventListener('click', () => this._openPanel());
      this._closeBtn?.addEventListener('click',  () => this._closePanel());
      this._overlay?.addEventListener('click',   () => this._closePanel());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this._panel && !this._panel.hidden) {
          this._closePanel();
        }
      });
    }

    _openPanel() {
      if (!this._panel) return;
      this._panel.removeAttribute('hidden');
      this._filterBtn?.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        this._overlay?.classList.add('is-visible');
        this._drawer?.classList.add('is-open');
      });
    }

    _closePanel() {
      if (!this._panel) return;
      this._overlay?.classList.remove('is-visible');
      this._drawer?.classList.remove('is-open');
      this._filterBtn?.setAttribute('aria-expanded', 'false');
      setTimeout(() => {
        this._panel.setAttribute('hidden', '');
        document.body.style.overflow = '';
      }, 300);
    }

    // ─────────────────────────────────────────────────
    // Filters
    // ─────────────────────────────────────────────────

    _bindFilters() {
      if (!this._form) return;

      this._form.addEventListener('change', () => {
        clearTimeout(this._countDebounce);
        this._countDebounce = setTimeout(() => this._updateFilters(), 350);
      });

      // Event delegation: survives the AJAX innerHTML swap in _updateFilters()
      this._form.addEventListener('click', (e) => {
        const tab = e.target.closest('.clp__filter-tab');
        if (tab) this._activateFilterTab(tab.dataset.filterTab);
      });

      this._applyBtn?.addEventListener('click', () => this._applyFilters());
      this._clearBtn?.addEventListener('click', () => this._clearFilters());
    }

    // Only one filter tab panel visible at a time; the active tab cannot be closed by re-clicking it.
    _activateFilterTab(index) {
      if (!this._form) return;

      this._form.querySelectorAll('.clp__filter-tab').forEach((btn) => {
        const isActive = btn.dataset.filterTab === index;
        btn.classList.toggle('clp__filter-tab--active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this._form.querySelectorAll('.clp__filter-tabpanel').forEach((panel) => {
        panel.hidden = panel.dataset.filterPanel !== index;
      });
    }

    // Reads the CURRENT form state (for count preview)
    _buildFormParams() {
      if (!this._form) return '';
      const params = new URLSearchParams();
      const data = new FormData(this._form);
      for (const [key, value] of data.entries()) {
        if (value !== '') params.append(key, value);
      }
      return params.toString();
    }

    // Builds params for grid fetches, using APPLIED (not pending) filters
    _buildParams(page = 1) {
      const params = new URLSearchParams(this._appliedParams || '');
      if (page > 1) params.set('page', page);
      return params;
    }

    async _updateFilters() {
      const formParams = this._buildFormParams();
      const url = `${this._collectionUrl}?${formParams}&section_id=${this._sectionId}`;

      if (this._countAbort) this._countAbort.abort();
      this._countAbort = new AbortController();

      try {
        const res  = await fetch(url, { signal: this._countAbort.signal });
        const text = await res.text();
        const doc  = new DOMParser().parseFromString(text, 'text/html');

        // Update result count on apply button
        const wrapper = doc.getElementById(`clp-grid-${this._sectionId}`);
        const count = parseInt(wrapper?.dataset.count ?? 0, 10);
        this._syncApplyBtn(count);

        // Remember which filter groups are expanded (accordion style)
        const openGroups = new Set();
        this._form.querySelectorAll('details[open]').forEach(d => {
          const label = d.querySelector('.clp__filter-group-label span')?.textContent?.trim();
          if (label) openGroups.add(label);
        });

        // Remember which filter tab is active (tabs style)
        const activeTabIndex = this._form.querySelector('.clp__filter-tab--active')?.dataset.filterTab;

        // Replace form content with server-updated filter options (new counts + disabled states)
        const newForm = doc.querySelector('.clp__filter-form');
        if (newForm && this._form) {
          this._form.innerHTML = newForm.innerHTML;
          // Restore expanded state
          this._form.querySelectorAll('details').forEach(d => {
            const label = d.querySelector('.clp__filter-group-label span')?.textContent?.trim();
            if (label && openGroups.has(label)) d.open = true;
          });
          // Restore active tab
          if (activeTabIndex !== undefined) this._activateFilterTab(activeTabIndex);
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.warn('[clp] filter update failed', e);
      }
    }

    async _applyFilters() {
      // Save pending form state as the new applied state
      this._appliedParams = this._buildFormParams();
      this._page    = 1;
      this._hasMore = true;

      await this._fetchProducts(true);

      this._closePanel();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });

      const newUrl = this._appliedParams
        ? `${this._collectionUrl}?${this._appliedParams}`
        : this._collectionUrl;
      history.pushState({}, '', newUrl);
    }

    _clearFilters() {
      if (!this._form) return;
      this._form.querySelectorAll('input[type="checkbox"]').forEach(cb  => { cb.checked = false; });
      this._form.querySelectorAll('input[type="number"]').forEach(inp  => { inp.value  = '';    });
      const firstRadio = this._form.querySelector('input[type="radio"]');
      if (firstRadio) firstRadio.checked = true;
      clearTimeout(this._countDebounce);
      this._countDebounce = setTimeout(() => this._updateFilters(), 350);
    }

    _syncApplyBtn(count) {
      if (!this._applyBtn) return;
      this._applyBtn.textContent = `SHOW RESULTS (${count})`;
    }

    // ─────────────────────────────────────────────────
    // Grid fetch (replace or append)
    // ─────────────────────────────────────────────────

    async _fetchProducts(replace = false) {
      if (this._loading) return;
      this._loading = true;
      this.classList.add('clp--loading');

      const params = this._buildParams(this._page);
      const url    = `${this._collectionUrl}?${params}&section_id=${this._sectionId}`;

      try {
        const res  = await fetch(url);
        const text = await res.text();
        const doc  = new DOMParser().parseFromString(text, 'text/html');

        const newWrapper = doc.getElementById(`clp-grid-${this._sectionId}`);
        const newGrid    = doc.getElementById(`clp-products-${this._sectionId}`);

        this._hasMore = newWrapper?.dataset.hasNext === 'true';

        if (!newGrid) {
          this._hasMore = false;
          return;
        }

        const items = Array.from(newGrid.querySelectorAll('.clp__item'));

        if (replace && this._grid) {
          this._grid.innerHTML = '';
        }

        if (this._grid) {
          items.forEach(item => this._grid.appendChild(item));
        }

      } catch (e) {
        console.warn('[clp] fetch failed', e);
        this._hasMore = false;
      } finally {
        this._loading = false;
        this.classList.remove('clp--loading');
        this._recheckSentinel();
      }
    }

    // ─────────────────────────────────────────────────
    // Infinite scroll
    // ─────────────────────────────────────────────────

    _initInfiniteScroll() {
      if (!this._sentinel) return;

      this._observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        if (this._loading || !this._hasMore) return;
        this._page++;
        this._fetchProducts(false);
      }, { rootMargin: '300px' });

      this._observer.observe(this._sentinel);
    }

    // IntersectionObserver only fires when visibility *changes*. If the sentinel is
    // still inside the trigger zone after content is added (tall screens, small
    // pages), re-observing forces a fresh callback. Terminates once hasMore is false.
    _recheckSentinel() {
      if (!this._observer || !this._sentinel || !this._hasMore) return;
      this._observer.unobserve(this._sentinel);
      this._observer.observe(this._sentinel);
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
      }
    }
  }

  if (!customElements.get('libertine-collection')) {
    customElements.define('libertine-collection', LibertineCollection);
  }
})();
